import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { FaUpload, FaEdit, FaTrash, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUserAlt } from "react-icons/fa";

// Define the backend API URL
const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
`;

const PageTitle = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 28px;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 40px;
`;

const FormGrid = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const FormSection = styled.div`
  grid-column: 1 / -1;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
  margin-bottom: 15px;
  
  h3 {
    font-size: 18px;
    color: #444;
    margin-bottom: 15px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #555;
  margin-bottom: 5px;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border: 2px dashed #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    border-color: #007bff;
    background-color: rgba(0, 123, 255, 0.03);
  }
`;

const ImagePreview = styled.div`
  width: 200px;
  height: 200px;
  margin-bottom: 15px;
  border-radius: 6px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-radius: 6px;
  font-size: 14px;
  
  svg {
    font-size: 16px;
  }
`;

const SubmitButton = styled.button`
  grid-column: 1 / -1;
  padding: 15px;
  background-color: ${props => props.isEdit ? '#ffa500' : '#007bff'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.isLoading ? 0.7 : 1};
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.isEdit ? '#e69500' : '#0056b3'};
  }
  
  &:disabled {
    background-color: #999;
  }
`;

const WorkshopList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const WorkshopCard = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const WorkshopImage = styled.div`
  height: 200px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WorkshopInfo = styled.div`
  padding: 15px;
`;

const WorkshopTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin-bottom: 5px;
`;

const WorkshopPrice = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #007bff;
  margin-bottom: 8px;
`;

const WorkshopMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  color: #666;
  
  svg {
    color: #555;
    font-size: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 14px;
  border: none;
  border-radius: 6px;
  background-color: ${props => props.variant === 'edit' ? '#ffa500' : '#e74c3c'};
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.variant === 'edit' ? '#e69500' : '#c0392b'};
  }
  
  svg {
    font-size: 14px;
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 16px;
`;

const AvailabilityBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.available > 0 ? '#e6f7ff' : '#fff1f0'};
  color: ${props => props.available > 0 ? '#1890ff' : '#f5222d'};
  border: 1px solid ${props => props.available > 0 ? '#91d5ff' : '#ffa39e'};
  margin-left: 8px;
`;

// Add new styled components for image navigation
const ImageNavButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const ImageNavButton = styled.button`
  background-color: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #e0e0e0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageCounter = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #666;
`;

const ImageThumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
`;

const ImageThumbnail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#007bff' : 'transparent'};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WorkshopAdmin = () => {
  const [workshops, setWorkshops] = useState([]);
  const [workshopForm, setWorkshopForm] = useState({
    name: "",
    instructor: "",
    description: "",
    time: "",
    date: "",
    location: "",
    slotsAvailable: "0",
    price: "0",
    duration: "60",
    category: "",
    featured: false,
    materials: ""
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [editWorkshop, setEditWorkshop] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = React.createRef();

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchWorkshops();
    
    // If ID is present in URL, load workshop for editing
    if (id) {
      fetchWorkshopById(id);
    }
  }, [id]);

  const fetchWorkshops = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/workshops`);
      setWorkshops(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching workshops:", error);
      setIsLoading(false);
    }
  };

  const fetchWorkshopById = async (workshopId) => {
    try {
      const response = await axios.get(`${API_URL}/workshops/${workshopId}`);
      const workshop = response.data;
      
      setWorkshopForm({
        name: workshop.name || "",
        instructor: workshop.instructor || "",
        description: workshop.description || "",
        time: workshop.time || "",
        date: workshop.date ? workshop.date.substring(0, 10) : "", // Format date for input
        location: workshop.location || "",
        slotsAvailable: workshop.slotsAvailable || "0",
        price: workshop.price || "0",
        duration: workshop.duration || "60",
        category: workshop.category || "",
        featured: workshop.featured || false,
        materials: workshop.materials || ""
      });
      
      // Handle both single imageUrl and array of imageUrls
      if (workshop.imageUrls && workshop.imageUrls.length > 0) {
        setPreviewImages(workshop.imageUrls);
        setPreviewImage(workshop.imageUrls[0]);
      } else if (workshop.imageUrl) {
        setPreviewImages([workshop.imageUrl]);
        setPreviewImage(workshop.imageUrl);
      }
      
      setEditWorkshop(workshop);
    } catch (error) {
      console.error("Error fetching workshop:", error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setImageFiles(files);

    // Create previews for all images
    const newPreviews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target.result);
        if (newPreviews.length === files.length) {
          setPreviewImages(newPreviews);
          setPreviewImage(newPreviews[0]); // Set the first image as the current preview
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWorkshopForm({
      ...workshopForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const uploadImagesToServer = async () => {
    if (!imageFiles || !imageFiles.length) {
      // If we're editing and there's an existing image URL, return it
      return editWorkshop?.imageUrls ? editWorkshop.imageUrls : [];
    }

    const formData = new FormData();
    imageFiles.forEach(file => {
      formData.append("image", file);
    });

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Return array of image URLs
      return response.data.imageUrls || [response.data.imageUrl];
    } catch (error) {
      console.error("Image upload error:", error);
      throw new Error("Failed to upload images");
    }
  };

  const handleWorkshopSubmit = async (e) => {
    e.preventDefault();

    if (!workshopForm.name || !workshopForm.date || !workshopForm.time) {
      alert("Name, date and time are required!");
      return;
    }

    try {
      setIsLoading(true);
      
      // Upload images if present
      let imageUrls = [];
      try {
        imageUrls = await uploadImagesToServer();
        console.log("Images uploaded successfully:", imageUrls);
      } catch (imageError) {
        console.error("Image upload failed:", imageError);
        // Continue even if image upload fails
      }

      // Prepare workshop data
      const workshopData = {
        name: workshopForm.name.trim(),
        instructor: workshopForm.instructor.trim(),
        description: workshopForm.description.trim(),
        time: workshopForm.time,
        date: workshopForm.date,
        location: workshopForm.location.trim(),
        slotsAvailable: parseInt(workshopForm.slotsAvailable) || 0,
        price: parseFloat(workshopForm.price) || 0,
        duration: parseInt(workshopForm.duration) || 60,
        category: workshopForm.category.trim(),
        featured: Boolean(workshopForm.featured),
        materials: workshopForm.materials.trim(),
        imageUrls: imageUrls // Array of all image URLs
      };

      console.log("Sending workshop data:", workshopData);

      let response;
      if (editWorkshop) {
        response = await axios.put(`${API_URL}/workshops/${editWorkshop._id}`, workshopData);
        console.log("Workshop updated successfully:", response.data);
        alert("Workshop updated successfully!");
      } else {
        response = await axios.post(`${API_URL}/workshops`, workshopData);
        console.log("Workshop created successfully:", response.data);
        alert("Workshop added successfully!");
      }

      // Reset form
      setWorkshopForm({
        name: "",
        instructor: "",
        description: "",
        time: "",
        date: "",
        location: "",
        slotsAvailable: "0",
        price: "0",
        duration: "60",
        category: "",
        featured: false,
        materials: ""
      });
      setImageFiles([]);
      setPreviewImages([]);
      setEditWorkshop(null);
      
      // Refresh workshops
      fetchWorkshops();
      
      // Redirect if editing
      if (id) {
        navigate("/workshopadmin");
      }
    } catch (error) {
      console.error("Workshop submission error:", error);
      if (error.response) {
        // The server responded with a status code outside the 2xx range
        console.error("Server response error:", error.response.data);
        alert(`Error ${editWorkshop ? 'updating' : 'creating'} workshop: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received:", error.request);
        alert("Error: No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request
        alert(`Error ${editWorkshop ? 'updating' : 'creating'} workshop: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleWorkshopDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this workshop?")) return;

    try {
      setIsLoading(true);
      await axios.delete(`${API_URL}/workshops/${id}`);
      setWorkshops(workshops.filter(w => w._id !== id));
      alert("Workshop deleted successfully!");
      setIsLoading(false);
    } catch (error) {
      console.error("Error deleting workshop:", error);
      alert("Error deleting workshop. Please try again.");
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Add image navigation functions
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? previewImages.length - 1 : prev - 1
    );
    setPreviewImage(previewImages[currentImageIndex === 0 ? previewImages.length - 1 : currentImageIndex - 1]);
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => 
      prev === previewImages.length - 1 ? 0 : prev + 1
    );
    setPreviewImage(previewImages[currentImageIndex === previewImages.length - 1 ? 0 : currentImageIndex + 1]);
  };

  return (
    <Container>
      <PageTitle>{editWorkshop ? "Edit Workshop" : "Add New Workshop"}</PageTitle>

      <FormContainer>
        <FormGrid onSubmit={handleWorkshopSubmit}>
          <FormSection>
            <h3>Basic Information</h3>
            
            <InputGroup>
              <Label>Workshop Name*</Label>
              <Input
                type="text"
                name="name"
                value={workshopForm.name}
                onChange={handleInputChange}
                placeholder="Enter workshop name"
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={workshopForm.description}
                onChange={handleInputChange}
                placeholder="Enter workshop description"
              />
            </InputGroup>
            
            <InputGroup>
              <Label>Category</Label>
              <Input
                type="text"
                name="category"
                value={workshopForm.category}
                onChange={handleInputChange}
                placeholder="Enter workshop category"
              />
            </InputGroup>
          </FormSection>
          
          <FormSection>
            <h3>Workshop Details</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <InputGroup>
                <Label>Instructor*</Label>
                <Input
                  type="text"
                  name="instructor"
                  value={workshopForm.instructor}
                  onChange={handleInputChange}
                  placeholder="Instructor name"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Location*</Label>
                <Input
                  type="text"
                  name="location"
                  value={workshopForm.location}
                  onChange={handleInputChange}
                  placeholder="Workshop location"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Date*</Label>
                <Input
                  type="date"
                  name="date"
                  value={workshopForm.date}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Time*</Label>
                <Input
                  type="time"
                  name="time"
                  value={workshopForm.time}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  name="duration"
                  value={workshopForm.duration}
                  onChange={handleInputChange}
                  placeholder="Duration in minutes"
                  min="0"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Materials Provided</Label>
                <Input
                  type="text"
                  name="materials"
                  value={workshopForm.materials}
                  onChange={handleInputChange}
                  placeholder="Materials provided"
                />
              </InputGroup>
              </div>
          </FormSection>
          
          <FormSection>
            <h3>Pricing & Availability</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <InputGroup>
                <Label>Price (₹)*</Label>
                <Input
                  type="number"
                  name="price"
                  value={workshopForm.price}
                  onChange={handleInputChange}
                  placeholder="Workshop price"
                  min="0"
                  step="0.01"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Available Slots*</Label>
                <Input
                  type="number"
                  name="slotsAvailable"
                  value={workshopForm.slotsAvailable}
                  onChange={handleInputChange}
                  placeholder="Number of available slots"
                  min="0"
                  required
                />
              </InputGroup>
              
              <InputGroup style={{ alignItems: "flex-start", marginTop: "10px" }}>
                <Label>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={workshopForm.featured}
                    onChange={handleInputChange}
                    style={{ marginRight: "8px" }}
                  />
                  Featured Workshop
                </Label>
              </InputGroup>
            </div>
          </FormSection>
          
          <FormSection>
            <h3>Workshop Images</h3>
            
            <ImageUploadContainer onClick={handleUploadClick}>
              {previewImage ? (
                <>
                  <ImagePreview>
                    <img src={previewImage} alt="Workshop preview" />
                  </ImagePreview>
                  
                  {previewImages.length > 1 && (
                    <>
                      <ImageCounter>
                        Image {currentImageIndex + 1} of {previewImages.length}
                      </ImageCounter>
                      
                      <ImageNavButtons>
                        <ImageNavButton onClick={(e) => {
                          e.stopPropagation();
                          handlePrevImage();
                        }} type="button">
                          ←
                        </ImageNavButton>
                        <ImageNavButton onClick={(e) => {
                          e.stopPropagation();
                          handleNextImage();
                        }} type="button">
                          →
                        </ImageNavButton>
                      </ImageNavButtons>
                      
                      <ImageThumbnails onClick={e => e.stopPropagation()}>
                        {previewImages.map((img, index) => (
                          <ImageThumbnail 
                            key={index} 
                            active={index === currentImageIndex}
                            onClick={() => {
                              setCurrentImageIndex(index);
                              setPreviewImage(img);
                            }}
                          >
                            <img src={img} alt={`Thumbnail ${index + 1}`} />
                          </ImageThumbnail>
                        ))}
                      </ImageThumbnails>
                    </>
                  )}
                </>
              ) : (
                <div style={{ padding: "30px 0" }}>
                  <FaUpload size={40} color="#999" />
                  <p style={{ marginTop: "15px", color: "#666" }}>
                    Click to upload workshop images
                  </p>
                </div>
              )}

              <FileInput
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                multiple
              />
              
              <UploadButton>
                <FaUpload /> {previewImages.length > 0 ? "Change Images" : "Upload Images"}
              </UploadButton>
            </ImageUploadContainer>
          </FormSection>
          
          <SubmitButton 
            type="submit" 
            disabled={isLoading || !workshopForm.name || !workshopForm.date || !workshopForm.time}
            isEdit={!!editWorkshop}
            isLoading={isLoading}
          >
            {isLoading 
              ? "Processing..." 
              : editWorkshop 
                ? "Update Workshop" 
                : "Add Workshop"}
          </SubmitButton>
        </FormGrid>
      </FormContainer>

      <PageTitle>Workshop List</PageTitle>
      
      {isLoading && <LoadingIndicator>Loading workshops...</LoadingIndicator>}
      
      <WorkshopList>
        {workshops.map((workshop) => (
          <WorkshopCard key={workshop._id}>
            <WorkshopImage>
              <img 
                src={(workshop.imageUrls && workshop.imageUrls.length > 0) ? workshop.imageUrls[0] : workshop.imageUrl || 'https://via.placeholder.com/300x200?text=No+Image'} 
                alt={workshop.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                }}
              />
            </WorkshopImage>
            
            <WorkshopInfo>
              <WorkshopTitle>
                {workshop.name}
                {workshop.featured && (
                  <span style={{ 
                    marginLeft: '8px', 
                    fontSize: '12px', 
                    backgroundColor: '#ffd700',
                    color: '#333',
                    padding: '3px 6px',
                    borderRadius: '4px'
                  }}>
                    Featured
                  </span>
                )}
              </WorkshopTitle>
              
              <WorkshopPrice>₹{workshop.price}
                <AvailabilityBadge available={workshop.slotsAvailable}>
                  {workshop.slotsAvailable > 0 
                    ? `${workshop.slotsAvailable} slots available` 
                    : 'Sold out'}
                </AvailabilityBadge>
              </WorkshopPrice>
              
              <WorkshopMeta>
                <FaUserAlt />
                <span>{workshop.instructor}</span>
              </WorkshopMeta>
              
              <WorkshopMeta>
                <FaCalendarAlt />
                <span>{formatDate(workshop.date)}</span>
              </WorkshopMeta>
              
              <WorkshopMeta>
                <FaClock />
                <span>{workshop.time} ({workshop.duration} min)</span>
              </WorkshopMeta>
              
              <WorkshopMeta>
                <FaMapMarkerAlt />
                <span>{workshop.location}</span>
              </WorkshopMeta>
              
              <ActionButtons>
                <ActionButton 
                  variant="edit" 
                  onClick={() => navigate(`/edit-workshop/${workshop._id}`)}
                >
                  <FaEdit /> Edit
                </ActionButton>
                
                <ActionButton 
                  variant="delete" 
                  onClick={() => handleWorkshopDelete(workshop._id)}
                >
                  <FaTrash /> Delete
                </ActionButton>
              </ActionButtons>
            </WorkshopInfo>
          </WorkshopCard>
        ))}
      </WorkshopList>
    </Container>
  );
};

export default WorkshopAdmin;